// ============================================================
// WorkspaceRightRail — the per-tab intelligence rail container.
//
// Organizes rail content into kicker-labeled sections (SOURCES,
// AGENTS, RECEIPTS, SIGNAL, …). Lives INSIDE a workspace tab —
// never a replacement for the OLIVIUM right panel and never a
// fourth panel mode.
//
//   <WorkspaceRightRail>
//     <WorkspaceRailSection label="SIGNAL">…</WorkspaceRailSection>
//     <WorkspaceRailSection label="AGENTS">…</WorkspaceRailSection>
//   </WorkspaceRightRail>
// ============================================================

import { WORKSPACE_FONT, WORKSPACE_SURFACE, WORKSPACE_TEXT, tint } from './workspaceTheme'

export function WorkspaceRailSection({ label, accent, badge, actions, children, style: styleOverride }) {
  return (
    <section style={{ minWidth: 0, ...styleOverride }}>
      {(label || actions) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            {accent && (
              <span
                aria-hidden
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: accent,
                  boxShadow: `0 0 8px ${tint(accent, 50)}`,
                  flexShrink: 0,
                }}
              />
            )}
            <span
              style={{
                fontSize: 9,
                letterSpacing: '0.26em',
                fontWeight: 700,
                textTransform: 'uppercase',
                color: WORKSPACE_TEXT.tertiary,
                fontFamily: WORKSPACE_FONT.mono,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </span>
            {badge != null && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: WORKSPACE_TEXT.muted,
                  fontFamily: WORKSPACE_FONT.mono,
                }}
              >
                {badge}
              </span>
            )}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  )
}

export default function WorkspaceRightRail({ children, gap = 20, style: styleOverride }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap,
        minWidth: 0,
        ...styleOverride,
      }}
    >
      {children}
    </div>
  )
}

// Small helper chip row used in rails (sources, agents, tags).
export function WorkspaceRailChips({ items = [], style: styleOverride }) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', ...styleOverride }}>
      {items.map((item, i) => {
        const label = typeof item === 'string' ? item : item.label
        const accent = typeof item === 'string' ? null : item.accent
        return (
          <span
            key={`${label}-${i}`}
            title={typeof item === 'string' ? undefined : item.hint}
            style={{
              fontSize: 9,
              fontWeight: 600,
              padding: '2px 7px',
              borderRadius: 4,
              fontFamily: WORKSPACE_FONT.mono,
              background: accent ? tint(accent, 8) : WORKSPACE_SURFACE.card,
              border: `1px solid ${accent ? tint(accent, 16) : WORKSPACE_SURFACE.lineFaint}`,
              color: accent || WORKSPACE_TEXT.tertiary,
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </span>
        )
      })}
    </div>
  )
}
